import { z } from 'zod';

export const PaginatedLinksSchema = z.object({
    first: z.string().nullable(),
    last: z.string().nullable(),
    prev: z.string().nullable(),
    next: z.string().nullable(),
});

export const CursorPaginatedMetaSchema = z.object({
    next_cursor: z.string().nullable(),
    path: z.string(),
    per_page: z.number().int(),
    prev_cursor: z.string().nullable(),
});

export type EmptyZodShape = z.ZodRawShape & Record<string, never>;

type CursorPaginatedBaseShape<T extends z.ZodTypeAny> = {
    data: z.ZodArray<T>;
    links: typeof PaginatedLinksSchema;
    meta: typeof CursorPaginatedMetaSchema;
};

type CursorPaginatedResponseShape<
    T extends z.ZodTypeAny,
    Extra extends z.ZodRawShape
> = CursorPaginatedBaseShape<T> & Extra;

export function createCursorPaginatedResponseSchema<T extends z.ZodTypeAny>(
    itemSchema: T
): z.ZodObject<CursorPaginatedBaseShape<T>>;
export function createCursorPaginatedResponseSchema<
    T extends z.ZodTypeAny,
    Extra extends z.ZodRawShape
>(itemSchema: T, extraShape: Extra): z.ZodObject<CursorPaginatedResponseShape<T, Extra>>;
export function createCursorPaginatedResponseSchema<
    T extends z.ZodTypeAny,
    Extra extends z.ZodRawShape
>(itemSchema: T, extraShape?: Extra) {
    const base = z.object({
        data: z.array(itemSchema),
        links: PaginatedLinksSchema,
        meta: CursorPaginatedMetaSchema,
    });

    return extraShape ? base.extend(extraShape) : base;
}

export type CursorPaginatedResponseSchema<
    T extends z.ZodTypeAny,
    Extra extends z.ZodRawShape = EmptyZodShape
> = z.ZodObject<CursorPaginatedResponseShape<T, Extra>>;

export type CursorPaginatedResponse<
    T extends z.ZodTypeAny,
    Extra extends z.ZodRawShape = EmptyZodShape
> = z.infer<CursorPaginatedResponseSchema<T, Extra>>;
